import React, { useId, useState } from 'react';
import styled from 'styled-components';
import type { PartsModelChoicePromptArtifact } from '../../services/chatApi';


const Card = styled.section`
  width: min(100%, 560px);
  margin-top: 14px;
  padding: 16px;
  border: 1px solid ${props => props.theme?.token?.colorBorder ?? '#d8dee8'};
  border-radius: 14px;
  background: ${props => props.theme?.token?.colorBgContainer ?? '#fff'};
  color: ${props => props.theme?.token?.colorText ?? '#172033'};
  box-shadow: 0 8px 24px rgba(23, 32, 51, 0.08);
`;

const Eyebrow = styled.div`
  margin-bottom: 5px;
  color: ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
`;

const Prompt = styled.p`
  margin: 5px 0 14px;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#596579'};
  font-size: 14px;
  line-height: 1.45;
`;

const ChoiceList = styled.ol`
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ChoiceButton = styled.button<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: 1px solid ${props => props.$selected
    ? (props.theme?.token?.colorPrimary ?? '#2672be')
    : (props.theme?.token?.colorBorder ?? '#c9d2df')};
  border-radius: 10px;
  background: ${props => props.$selected
    ? 'rgba(38, 114, 190, 0.08)'
    : (props.theme?.token?.colorBgContainer ?? '#fff')};
  color: ${props => props.theme?.token?.colorText ?? '#172033'};
  cursor: pointer;
  font: inherit;
  text-align: left;

  &:hover:not(:disabled) {
    border-color: ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
    background: rgba(38, 114, 190, 0.05);
  }

  &:focus-visible {
    outline: 3px solid ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }
`;

const ChoiceNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: ${props => props.theme?.token?.colorFillTertiary ?? '#edf2f7'};
  color: ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
  font-size: 13px;
  font-weight: 800;
`;

const ChoiceText = styled.span`
  min-width: 0;
`;

const ChoiceLabel = styled.span`
  display: block;
  overflow-wrap: anywhere;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
`;

const ChoiceDescription = styled.span`
  display: block;
  margin-top: 2px;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#596579'};
  font-size: 12px;
  line-height: 1.35;
`;

const Choosing = styled.span`
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#596579'};
  font-size: 12px;
  white-space: nowrap;
`;

const Status = styled.p`
  margin: 12px 0 0;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#596579'};
  font-size: 13px;
  line-height: 1.4;
`;

const ErrorText = styled.p`
  margin: 12px 0 0;
  color: ${props => props.theme?.token?.colorError ?? '#b42318'};
  font-size: 13px;
`;

export type PartsModelChoice = PartsModelChoicePromptArtifact['choices'][number];

interface Props {
  artifact: PartsModelChoicePromptArtifact;
  disabled?: boolean;
  disabledMessage?: string;
  onSelect: (choice: PartsModelChoice) => Promise<void> | void;
}

const PartsModelChoiceCard: React.FC<Props> = ({
  artifact,
  disabled = false,
  disabledMessage,
  onSelect,
}) => {
  const headingId = useId();
  const promptId = useId();
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const expired = Boolean(
    artifact.expires_at
    && Number.isFinite(Date.parse(artifact.expires_at))
    && Date.parse(artifact.expires_at) <= Date.now(),
  );
  const unavailable = disabled || expired || submitting || submitted;

  const choose = async (choice: PartsModelChoice) => {
    if (unavailable) return;
    setError('');
    setSelectedChoice(choice.choice_id);
    setSubmitting(true);
    try {
      await onSelect(choice);
      setSubmitted(true);
    } catch (selectionError) {
      setSelectedChoice(null);
      setError(
        selectionError instanceof Error && selectionError.message
          ? `${selectionError.message} Please try again.`
          : 'Unable to select that model. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card aria-labelledby={headingId} aria-describedby={promptId} aria-busy={submitting}>
      <Eyebrow>Model selection</Eyebrow>
      <Title id={headingId}>{artifact.title}</Title>
      <Prompt id={promptId}>{artifact.prompt}</Prompt>
      <ChoiceList>
        {artifact.choices.map((choice, index) => {
          const choiceNumber = index + 1;
          const selected = selectedChoice === choice.choice_id;
          return (
            <li key={choice.choice_id}>
              <ChoiceButton
                type="button"
                $selected={selected}
                disabled={unavailable}
                aria-label={`Select option ${choiceNumber}: model ${choice.label}`}
                onClick={() => void choose(choice)}
              >
                <ChoiceNumber aria-hidden="true">{choiceNumber}</ChoiceNumber>
                <ChoiceText>
                  <ChoiceLabel>{choice.label}</ChoiceLabel>
                  {choice.description && (
                    <ChoiceDescription>{choice.description}</ChoiceDescription>
                  )}
                </ChoiceText>
                {selected && (
                  <Choosing aria-live="polite">
                    {submitted ? 'Selected' : 'Selecting…'}
                  </Choosing>
                )}
              </ChoiceButton>
            </li>
          );
        })}
      </ChoiceList>
      {(expired || (disabledMessage && !selectedChoice)) && (
        <Status role="status">
          {expired
            ? 'This model selection expired. Ask SmartDocs to list the models again.'
            : disabledMessage}
        </Status>
      )}
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Card>
  );
};

export default PartsModelChoiceCard;
